<script>
    import { onMount } from "svelte";
    import { fade } from "svelte/transition";
    import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
    import { authState } from "@app/store/authStore.svelte.js";
    import { uiState } from "@app/store/uiStore.svelte.js";
    import {
        knpState,
        loadKnpGuides,
        saveKnpGuide,
        selectKnpGuide,
    } from "@app/store/knpStore.svelte.js";
    import { toast } from "@zerodevx/svelte-toast";
    import { v4 as uuidv4 } from "uuid";
    import Swal from "sweetalert2";
    import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
    import firebase from "@app/configs/firebase.js";
    import db from "@app/configs/firestore.js";
    import KnpGuides from "./KnpGuides.svelte";
    import KnpTerms from "./KnpTerms.svelte";
    import * as m from "@app/paraglide/messages.js";

    let activeTab = $state("details");
    let availableGroups = $state([]);
    let loadingGroups = $state(false);

    // Derived state for admin/manager check
    let isAdminOrManager = $derived(
        authState.user && (authState.user.role === "admin" || authState.user.role === "manager")
    );

    // Load available groups from Firestore
    async function loadAvailableGroups() {
        if (!authState.team?.id) return;
        
        loadingGroups = true;
        try {
            const teamId = authState.team.id;
            const groupsSnapshot = await db.collection('teams').doc(teamId).collection('groups').get();
            availableGroups = [];
            groupsSnapshot.forEach(doc => {
                availableGroups.push(doc.data());
            });
        } catch (error) {
            console.error("Error loading groups:", error);
            toast.push(m.knp_manager_failed_load_groups({ error: error.message }), {
                classes: ["toast-danger"],
            });
        } finally {
            loadingGroups = false;
        }
    }

    // Manage guide permissions using SweetAlert
    async function manageGuidePermissions() {
        if (!knpState.selectedGuide) return;

        try {
            // Load available groups
            await loadAvailableGroups();
            
            // Build checkboxes for each group
            const groupInputs = availableGroups.map(group => {
                const isAssigned = knpState.selectedGuide.groups && knpState.selectedGuide.groups.includes(group.id);
                return `
                    <div class="form-check mb-2">
                        <input class="form-check-input" type="checkbox" 
                               id="group-${group.id}" ${isAssigned ? 'checked' : ''}>
                        <label class="form-check-label" for="group-${group.id}">
                            ${group.name}
                        </label>
                        ${group.description ? `<div class="small text-muted">${group.description}</div>` : ''}
                    </div>
                `;
            }).join('');
            
            // Show SweetAlert with checkboxes
            const result = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.confirmCancel,
                title: m.knp_manager_permissions_title(),
                html: `
                    <div class="text-start">
                        <p>${m.knp_manager_permissions_select_groups()}</p>
                        <p class="text-secondary small">
                            ${m.knp_manager_permissions_note()}
                        </p>
                        ${availableGroups.length === 0 
                            ? `<div class="alert alert-info">${m.knp_manager_no_groups_available()}</div>` 
                            : `<div class="mt-3">${groupInputs}</div>`
                        }
                    </div>
                `,
                showCancelButton: true,
                confirmButtonText: m.knp_manager_save_permissions(),
                cancelButtonText: m.cancel(),
                width: '600px',
                preConfirm: () => {
                    return availableGroups
                        .filter(group => document.getElementById(`group-${group.id}`)?.checked)
                        .map(group => group.id);
                }
            });
            
            if (result.isConfirmed) {
                const selectedGroups = result.value;
                
                // Update Firestore
                const teamId = authState.team.id;
                const knpRef = db.collection("teams").doc(teamId).collection("knp");
                await knpRef.doc(knpState.selectedGuide.id).update({
                    groups: selectedGroups,
                    updatedOn: firebase.firestore.FieldValue.serverTimestamp()
                });
                
                // Update local state
                knpState.selectedGuide.groups = selectedGroups;
                knpState.guides = knpState.guides.map(g => 
                    g.id === knpState.selectedGuide.id ? {...g, groups: selectedGroups} : g
                );
                
                toast.push(m.knp_manager_permissions_updated(), {
                    classes: ["toast-success"],
                });
            }
        } catch (error) {
            console.error("Error managing guide permissions:", error);
            toast.push(m.knp_manager_permissions_update_failed({ error: error.message }), {
                classes: ["toast-danger"],
            });
        }
    }

    // Load guides when modal opens
    onMount(() => {
        uiState.knpLookup = false;        
        loadKnpGuides();
    });

    async function createNewGuide() {
        const newGuide = {
            id: uuidv4(),
            title: m.knp_manager_new_guide_title(),
            description: "",
            original_language: "en-US",
            languages: ["en-US"]
        };

        let savedGuide = await saveKnpGuide(newGuide);
        toast.push(m.knp_manager_new_guide_created(), {
            classes: ["toast-success"],
        });

        // Select the saved guide from the guides array to ensure we have the correct reference
        const guideFromStore = knpState.guides.find(g => g.id === savedGuide.id);
        if (guideFromStore) {
            selectKnpGuide(guideFromStore);
        }
    }
</script>
<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'knpManager' ? 'show d-block' : ''}"
    role="dialog"
    aria-labelledby="knpManagerTitle"
    tabindex="-1"
>
    <div class="modal-dialog modal-fullscreen" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="knpManagerTitle">{m.knp_manager_title()}</h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label={m.close()}
                    onclick={hideModal}
                ></button>
            </div>
            <div class="modal-body">
                {#if !authState.team?.id}
                    <div class="alert alert-warning" role="alert">
                        <i class="bi bi-exclamation-triangle-fill me-2"></i>
                        {m.knp_manager_team_required()}
                    </div>
                {:else}
                    <div class="row" style="min-height: 500px;">
                        <!-- Left Panel: Guide List -->
                        <div class="col-3 border-end">
                            <div class="d-flex justify-content-between align-items-center mb-3">
                                <h6 class="mb-0">{m.knp_manager_guides_title()}</h6>
                                <button
                                    class="btn btn-sm btn-primary"
                                    onclick={createNewGuide}
                                    title={m.knp_manager_create_guide_title()}
                                >
                                    <i class="bi bi-plus-lg"></i>
                                </button>
                            </div>
                            
                            {#if knpState.loading}
                                <div class="text-center">
                                    <div class="spinner-border spinner-border-sm" role="status">
                                        <span class="visually-hidden">{m.knp_manager_loading()}</span>
                                    </div>
                                </div>
                            {:else if knpState.guides.length === 0}
                                <p class="text-muted small">{m.knp_manager_no_guides()}</p>
                            {:else}
                                <div class="list-group">
                                    {#each knpState.guides as guide (guide.id)}
                                        <button
                                            class="list-group-item list-group-item-action {knpState.selectedGuide?.id === guide.id ? 'active' : ''}"
                                            onclick={() => selectKnpGuide(guide)}
                                        >
                                            <div class="d-flex w-100 justify-content-between">
                                                <h6 class="mb-1">{guide.title}</h6>                                               
                                            </div>
                                            <small>{m.knp_manager_languages_count({ count: guide.languages?.length || 0 })}</small>
                                        </button>
                                    {/each}
                                </div>
                            {/if}
                        </div>

                        <!-- Right Panel: Guide Details and Terms -->
                        <div class="col-9">
                            {#if knpState.selectedGuide}
                                <!-- Tabs -->
                                <ul class="nav nav-tabs mb-3" role="tablist">
                                    <li class="nav-item" role="presentation">
                                        <button
                                            class="nav-link {activeTab === 'details' ? 'active' : ''}"
                                            onclick={() => activeTab = "details"}
                                            type="button"
                                            role="tab"
                                        >
                                            {m.knp_manager_guide_details_tab()}
                                        </button>
                                    </li>
                                    <li class="nav-item" role="presentation">
                                        <button
                                            class="nav-link {activeTab === 'terms' ? 'active' : ''}"
                                            onclick={() => activeTab = "terms"}
                                            type="button"
                                            role="tab"
                                        >
                                            {m.knp_manager_terms_tab({ count: knpState.terms.length })}
                                        </button>
                                    </li>
                                </ul>

                                <!-- Tab Content -->
                                <div class="tab-content">
                                    {#if activeTab === "details"}
                                        <KnpGuides 
                                            {manageGuidePermissions}
                                            {isAdminOrManager}
                                        />
                                    {:else if activeTab === "terms"}
                                        <KnpTerms />
                                    {/if}
                                </div>
                            {:else}
                                <div class="text-center text-muted py-5">
                                    <i class="bi bi-journal-text" style="font-size: 3rem;"></i>
                                    <p class="mt-3">{m.knp_manager_select_guide_prompt()}</p>
                                </div>
                            {/if}
                        </div>
                    </div>
                {/if}
            </div>
        </div>
    </div>
</div>

<style>
    :global(.knp-datagrid-container) {
        border: 1px solid #dee2e6;
        border-radius: 0.375rem;
        overflow: hidden;
    }
</style>